import api from '../api/client';

const SDK_ID = 'razorpay-sdk';
const SDK_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

// Injects Razorpay's checkout script once and resolves when it is usable.
export const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById(SDK_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(!!window.Razorpay));
      existing.addEventListener('error', () => resolve(false));
      return undefined;
    }
    const el = document.createElement('script');
    el.id = SDK_ID;
    el.src = SDK_SRC;
    el.async = true;
    el.onload = () => resolve(!!window.Razorpay);
    el.onerror = () => resolve(false);
    document.body.appendChild(el);
    return undefined;
  });

/**
 * Takes payment for an order that already exists on the server.
 * The amount and Razorpay order are created server-side, and the signature is
 * verified server-side, so nothing here can be tampered with from the browser.
 *
 * Resolves { paid: boolean, reason?: string }.
 */
export const payForOrder = async ({ order, settings, onToast }) => {
  const toast = onToast || (() => {});
  const ok = await loadRazorpay();
  if (!ok) {
    toast('Could not load the payment window. Your order is saved — you can pay from the order page.', 'error');
    return { paid: false, reason: 'sdk' };
  }

  let init;
  try {
    init = await api.post('/payments/razorpay/create', { orderId: order._id });
  } catch (e) {
    toast(e.message, 'error');
    return { paid: false, reason: 'create' };
  }

  return new Promise((resolve) => {
    const rz = new window.Razorpay({
      key: init.keyId,
      amount: init.amount,
      currency: init.currency,
      name: settings?.general?.siteName || 'Checkout',
      description: `Order ${init.orderNumber}`,
      order_id: init.razorpayOrderId,
      prefill: init.prefill,
      notes: { orderNumber: init.orderNumber },
      theme: { color: settings?.general?.primaryColor || '#000000' },
      handler: async (response) => {
        try {
          // Only the server can confirm this — it re-computes the HMAC signature.
          const res = await api.post('/payments/razorpay/verify', { orderId: order._id, ...response });
          resolve({ paid: true, order: res.order });
        } catch (err) {
          toast(err.message || 'We could not verify the payment', 'error');
          resolve({ paid: false, reason: 'verify' });
        }
      },
      modal: {
        ondismiss: () => resolve({ paid: false, reason: 'dismissed' }),
      },
    });
    rz.on('payment.failed', (e) => {
      toast(e?.error?.description || 'The payment failed', 'error');
      resolve({ paid: false, reason: 'failed' });
    });
    rz.open();
  });
};
